import { useEffect, useCallback, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Clock,
  MapPin,
  Check,
  ChevronLeft,
  MessageCircle,
  Mail,
  Phone,
  ChevronRight,
  AlertCircle,
  Calendar,
  Ticket,
} from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { BookingModal } from '../components/BookingModal';
import { TourInquiryForm } from '../components/TourInquiryForm';

export default function TripDetail() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  // Embla — battle-tested for mobile touch. dragFree=false to snap, containScroll to hide dead space.
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'center',
    containScroll: 'trimSnaps',
    dragFree: false,
    watchDrag: true,
    skipSnaps: false,
    duration: 24, // snap animation ms
  });

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveSlide(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    onSelect();
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    api.get(`/trips/${slug}`).then(({ data }) => setTrip(data)).catch(() => setTrip(null)).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (window.location.hash === '#booking') {
      setTimeout(() => {
        const el = document.getElementById('booking');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, [trip]);

  if (loading) {
    return (
      <div className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-96 rounded-3xl bg-white/5 border border-white/10 animate-pulse" />
      </div>
    );
  }
  if (!trip) return <Navigate to="/trips" replace />;

  const wa = trip.whatsapp_url || settings?.whatsapp_url || '';
  const gallery = Array.isArray(trip.gallery) && trip.gallery.length ? trip.gallery : (trip.image_url ? [trip.image_url] : []);

  return (
    <div className="pt-24 pb-24">
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 hero-radial pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 relative">
          <Link to="/trips" className="inline-flex items-center gap-1.5 text-xs text-white/65 hover:text-white mb-6">
            <ChevronLeft className="h-3.5 w-3.5" /> Back to all trips
          </Link>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#00b4d8] font-semibold mb-2">
            <Link to="/" className="hover:text-white">Home</Link> <ChevronRight className="inline h-3 w-3 mx-1" /> Oman Trips <ChevronRight className="inline h-3 w-3 mx-1" /> {trip.name}
          </div>
          <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-4xl">{trip.name}</h1>
          <div className="mt-5 flex flex-wrap gap-3 text-sm text-white/75">
            {trip.duration && <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-[#00b4d8]" /> {trip.duration}</span>}
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#00b4d8]" /> Musandam, Oman</span>
            {trip.price && <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4 text-[#00b4d8]" /> from <span className="text-[#c8e600] font-semibold">{trip.price}</span> per person</span>}
          </div>
        </div>
      </section>

      {/* GALLERY — Embla carousel: rock-solid mobile touch swipe */}
      {gallery.length > 0 && (
        <section className="py-6">
          <div className="px-0 sm:max-w-7xl sm:mx-auto sm:px-6 lg:px-8">
            <div className="relative">
              {/*
                Embla mounts a viewport (overflow:hidden) + container (flex).
                Touch, drag momentum, snap — all handled natively across
                iOS Safari, Chrome Android, and desktop browsers.
              */}
              <div
                ref={emblaRef}
                data-testid="trip-gallery-viewport"
                className="overflow-hidden"
                style={{ paddingLeft: 'max(env(safe-area-inset-left), 16px)', paddingRight: 'max(env(safe-area-inset-right), 16px)' }}
              >
                <div className="flex gap-3" data-testid="trip-gallery-scroller">
                  {gallery.map((src, i) => (
                    <div
                      key={`${src}-${i}`}
                      data-testid={`trip-gallery-slide-${i}`}
                      className="relative flex-[0_0_90%] sm:flex-[0_0_60%] md:flex-[0_0_50%] lg:flex-[0_0_42%] min-w-0 rounded-2xl overflow-hidden bg-[#0d223d] aspect-[4/3]"
                    >
                      <img
                        src={src}
                        alt={`${trip.name} ${i + 1}`}
                        draggable={false}
                        className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
                        loading={i === 0 ? 'eager' : 'lazy'}
                        style={{ WebkitUserDrag: 'none' }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/45 via-transparent to-transparent pointer-events-none" />
                      <div className="absolute bottom-3 left-3 text-[11px] uppercase tracking-[0.2em] font-semibold text-white/85 pointer-events-none">
                        {String(i + 1).padStart(2, '0')} / {String(gallery.length).padStart(2, '0')}
                      </div>
                      {/* Expand button — small target so drags never accidentally open it */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setLightbox(src); }}
                        aria-label="Open photo"
                        data-testid={`trip-gallery-expand-${i}`}
                        className="absolute top-2.5 right-2.5 h-9 w-9 inline-flex items-center justify-center rounded-full bg-[#0a1628]/75 border border-white/15 text-white backdrop-blur-sm hover:bg-[#0a1628] active:scale-95 transition"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                          <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {gallery.length > 1 && (
                <>
                  {/* Desktop arrows */}
                  <button
                    type="button"
                    aria-label="Previous photo"
                    data-testid="trip-gallery-prev"
                    onClick={scrollPrev}
                    className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-[#0a1628]/80 border border-white/20 text-white hover:bg-[#0a1628] transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Next photo"
                    data-testid="trip-gallery-next"
                    onClick={scrollNext}
                    className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-[#0a1628]/80 border border-white/20 text-white hover:bg-[#0a1628] transition-colors"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>

                  {/* Dot indicator — tap-friendly on mobile */}
                  <div className="mt-3 flex items-center justify-center gap-1.5">
                    {gallery.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        aria-label={`Go to photo ${i + 1}`}
                        data-testid={`trip-gallery-dot-${i}`}
                        onClick={() => emblaApi?.scrollTo(i)}
                        className={`h-1.5 rounded-full transition-all duration-200 ${i === activeSlide ? 'w-6 bg-[#00b4d8]' : 'w-1.5 bg-white/25 hover:bg-white/50'}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* MAIN CONTENT */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
          {/* LEFT: long copy */}
          <div className="lg:col-span-2 space-y-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#00b4d8] font-semibold mb-3">The Experience</div>
              <div className="space-y-4 text-white/80 text-base leading-relaxed whitespace-pre-line">
                {trip.description || (
                  <span className="text-white/50 italic">Full itinerary coming soon — reach out below and our team will share dates, what to bring, pickup points and pricing.</span>
                )}
              </div>
            </div>

            {/* What's included */}
            {Array.isArray(trip.includes) && trip.includes.length > 0 && (
              <Card className="rounded-3xl border-white/10 bg-white/[0.04] p-6 sm:p-8">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#c8e600] font-semibold mb-3">What’s included</div>
                <h3 className="font-display text-2xl font-semibold">Everything you need for the day</h3>
                <ul className="mt-5 grid sm:grid-cols-2 gap-3">
                  {trip.includes.map((inc, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/80">
                      <Check className="h-4 w-4 mt-0.5 text-[#c8e600] shrink-0" /> <span>{inc}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Visa policy tabs */}
            {(trip.visa_policy_residence || trip.visa_policy_tourist) && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="h-4 w-4 text-[#c8e600]" />
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#c8e600] font-semibold">Important: Oman Visa Policy</div>
                </div>
                <Tabs defaultValue="residence" className="w-full">
                  <TabsList className="bg-white/5 border border-white/10 rounded-xl">
                    <TabsTrigger value="residence" data-testid="visa-tab-residence" className="data-[state=active]:bg-[#00b4d8] data-[state=active]:text-[#0a1628] rounded-lg">UAE Residence</TabsTrigger>
                    <TabsTrigger value="tourist" data-testid="visa-tab-tourist" className="data-[state=active]:bg-[#00b4d8] data-[state=active]:text-[#0a1628] rounded-lg">UAE Tourist</TabsTrigger>
                  </TabsList>
                  <TabsContent value="residence" className="mt-4">
                    <Card className="rounded-2xl border-white/10 bg-white/[0.04] p-6">
                      <div className="text-sm text-white/75 leading-relaxed whitespace-pre-line">{trip.visa_policy_residence}</div>
                    </Card>
                  </TabsContent>
                  <TabsContent value="tourist" className="mt-4">
                    <Card className="rounded-2xl border-white/10 bg-white/[0.04] p-6">
                      <div className="text-sm text-white/75 leading-relaxed whitespace-pre-line">{trip.visa_policy_tourist}</div>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>

          {/* RIGHT: sticky booking card */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-4" id="booking">
              <Card className="rounded-3xl border-white/10 bg-white/[0.04] p-6 backdrop-blur">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#c8e600] font-semibold mb-1">Ready to dive in?</div>
                <h3 className="font-display text-2xl font-semibold leading-snug">Book your seat</h3>
                {trip.price && (
                  <div className="mt-3">
                    <div className="flex items-baseline flex-wrap gap-x-2 gap-y-1" data-testid="trip-detail-price">
                      <span className="font-display text-3xl font-semibold text-white">{trip.price}</span>
                      {trip.compare_at_price && (() => {
                        const p = parseFloat(String(trip.price).replace(/,/g, '').match(/\d+(?:\.\d+)?/)?.[0] || 0);
                        const c = parseFloat(String(trip.compare_at_price).replace(/,/g, '').match(/\d+(?:\.\d+)?/)?.[0] || 0);
                        if (c > p) {
                          const pct = Math.round(((c - p) / c) * 100);
                          return (
                            <>
                              <span className="text-base text-white/45 line-through decoration-white/40">{trip.compare_at_price}</span>
                              <span className="inline-flex items-center rounded-full bg-red-500/20 text-red-300 border border-red-400/30 font-bold text-[11px] px-2 py-0.5">−{pct}% OFF</span>
                            </>
                          );
                        }
                        return null;
                      })()}
                      <span className="text-xs text-white/55 basis-full sm:basis-auto">per person</span>
                    </div>
                  </div>
                )}
                <p className="mt-3 text-xs text-white/65">Pickup from your hotel in Dubai, Sharjah or Ras Al Khaimah. Reach out and we’ll confirm dates, head-count and any extras.</p>
                <div className="mt-5 space-y-2">
                  <Button
                    data-testid="trip-booking-open"
                    onClick={() => setBookingOpen(true)}
                    className="w-full bg-[#0078d4] hover:bg-[#0066b8] text-white font-semibold rounded-xl h-12 text-base shadow-lg shadow-[#0078d4]/30"
                  >
                    <Ticket className="mr-2 h-5 w-5" /> Book Trip
                  </Button>
                  {wa ? (
                    <a href={wa} target="_blank" rel="noopener noreferrer" className="block">
                      <Button
                        data-testid="trip-booking-whatsapp"
                        variant="outline"
                        className="w-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-xl h-11"
                      >
                        <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Us
                      </Button>
                    </a>
                  ) : (
                    <Button
                      data-testid="trip-booking-whatsapp-disabled"
                      disabled
                      variant="outline"
                      className="w-full border-white/15 bg-white/5 text-white/40 rounded-xl h-11"
                    >
                      <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp coming soon
                    </Button>
                  )}
                </div>
                <div className="mt-5 pt-5 border-t border-white/10 space-y-2 text-sm">
                  {settings?.contact_email && (
                    <a href={`mailto:${settings.contact_email}?subject=Booking%20%E2%80%93%20${encodeURIComponent(trip.name)}`} className="flex items-center gap-2 text-white/75 hover:text-white">
                      <Mail className="h-4 w-4 text-[#00b4d8]" /> {settings.contact_email}
                    </a>
                  )}
                  {settings?.contact_phone && (
                    <a href={`tel:${settings.contact_phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-white/75 hover:text-white">
                      <Phone className="h-4 w-4 text-[#00b4d8]" /> {settings.contact_phone}
                    </a>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Sales lead-capture section — replicates the "Book a Tour with Us" pattern */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TourInquiryForm
            tripSlug={trip.slug}
            tripName={trip.name}
            title="Book a Tour with Us"
            subtitle={trip.short_description || `Reach out to reserve your ${trip.name} experience — our team replies within a few hours.`}
          />
        </div>
      </section>

      {/* Floating mobile booking bar */}
      <div className="lg:hidden fixed bottom-4 left-4 right-4 z-30">
        <Button
          data-testid="trip-booking-open-mobile"
          onClick={() => setBookingOpen(true)}
          className="w-full bg-[#0078d4] hover:bg-[#0066b8] text-white font-semibold rounded-2xl h-14 text-base shadow-xl shadow-[#0078d4]/40"
        >
          <Ticket className="mr-2 h-5 w-5" /> Book Trip
        </Button>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-[#0a1628]/95 backdrop-blur flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-w-full max-h-full rounded-2xl object-contain" />
        </div>
      )}

      {/* Booking modal */}
      <BookingModal
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        tripSlug={trip.slug}
        tripName={trip.name}
      />
    </div>
  );
}
